import { ChangeEvent, ChangeEventHandler, KeyboardEvent, useContext } from 'react';
import useStyles from './Back.styles';
import { UseStylesProps } from 'types/utils/UseStylesProps';
import { Grid, TextField } from '@mui/material';
import PostcardStamp from 'assets/images/postcard/postcard_stamp.png';
import Image from 'components/medias/Image';
import { useWebUserContext, useSelectedLinkContext } from 'contexts';
import { User } from 'types/User';
import { postcardContext } from 'contexts/Communicate/Postcard/PostcardContext';
import { useIntl } from 'react-intl';
import { getOnKeyPressFunction, POSTCARD_CONTENT_MAX_LENGTH } from './handlers';

type BackPureProps = UseStylesProps & {
  postcardContent: string;
  recipient: User | undefined;
  sender: string;
  handleContentChange: ChangeEventHandler;
  onKeyPress: (e: KeyboardEvent) => void;
};

export const BackPure = ({
  classes,
  postcardContent,
  recipient,
  sender,
  handleContentChange,
  onKeyPress,
}: BackPureProps): JSX.Element => {
  const intl = useIntl();
  return (
    <div className={classes.container}>
      <Grid className={classes.inner} container>
        <Grid className={classes.leftPart} item xs={8}>
          <Grid className={classes.leftPartContainer} container>
            <Grid className={classes.contentContainer} item xs={12}>
              <TextField
                className={classes.content}
                data-testid="postcard-content-textfield"
                maxRows={10}
                multiline
                placeholder={intl.formatMessage({
                  id: 'form.placeholder.writeTextHere',
                })}
                value={postcardContent}
                onChange={handleContentChange}
                onKeyPress={onKeyPress}
              />
            </Grid>

            <Grid item xs={12}>
              <div
                className={classes.contentLength}
                data-testid="postcard-content-length"
              >
                ({postcardContent.length}/{POSTCARD_CONTENT_MAX_LENGTH})
              </div>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.sender} data-testid="postcard-sender">
                {sender}
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid className={classes.rightPart} item xs={4}>
          <div className={classes.rightPartContainer}>
            <Image className={classes.stamp} height={120} src={PostcardStamp} />

            <Grid className={classes.recipient} container>
              <Grid
                className={classes.address}
                data-testid="postcard-recipient"
                item
                xs={12}
              >
                {recipient?.firstName} {recipient?.lastName}
              </Grid>

              <Grid
                className={classes.address}
                data-testid="postcard-recipient-address"
                item
                xs={12}
              >
                {recipient?.postalAddress.address.trim()}
              </Grid>

              <Grid
                className={classes.address}
                data-testid="postcard-recipient-city"
                item
                xs={12}
              >
                {recipient?.postalAddress.zipcode}{' '}
                {recipient?.postalAddress.city}
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

const Back = (): JSX.Element | null => {
  const classes = useStyles();

  const { postcardContent, setPostcardContent } = useContext(postcardContext);
  const { webUser } = useWebUserContext();
  const { selectedLink } = useSelectedLinkContext();
  const sender = `${webUser.firstName} ${webUser.lastName}`;

  if (!selectedLink) {
    return null;
  }

  const handleContentChange = (e : ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    if (value.length <= POSTCARD_CONTENT_MAX_LENGTH) {
      setPostcardContent(value);
    }
  };

  const onKeyPress = getOnKeyPressFunction({ postcardContent });

  return (
    <BackPure
      classes={classes}
      postcardContent={postcardContent}
      recipient={selectedLink.mainUser}
      sender={sender}
      handleContentChange={handleContentChange}
      onKeyPress={onKeyPress}
    />
  );
};

export default Back;
